import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Search, Loader2, Receipt, User, CreditCard, Banknote, Building, Mail } from "lucide-react";
import { AccountPaymentReceiptModal } from "./AccountPaymentReceiptModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountReceiptModal = ({ open, onClose }) => {
  const [step, setStep] = useState("search"); // search, payment
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [fetchingBalance, setFetchingBalance] = useState(false);
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [cashAmount, setCashAmount] = useState("");
  const [cardAmount, setCardAmount] = useState("");
  const [eftAmount, setEftAmount] = useState("");
  const [note, setNote] = useState("");
  const [activeInput, setActiveInput] = useState("cash");
  
  // Receipt state
  const [completedPayment, setCompletedPayment] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  
  // Email prompt state
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailPromptAddress, setEmailPromptAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  // Opt-in: cashier ticks to email the customer a receipt. Default OFF.
  const [emailReceiptToCustomer, setEmailReceiptToCustomer] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCustomers();
    } else {
      // Reset state when closing
      setStep("search");
      setSelectedCustomer(null);
      setSearchQuery("");
      setCashAmount("");
      setCardAmount("");
      setEftAmount("");
      setNote("");
      setPaymentMethod("CASH");
      setActiveInput("cash");
      setCompletedPayment(null);
      setShowReceipt(false);
      setShowEmailPrompt(false);
      setEmailPromptAddress("");
      setEmailReceiptToCustomer(false);
    }
  }, [open]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`, {
        params: {
          include_calculated_balance: true,  // Use calculated balance, not stored account_balance
          limit: 2000,
        }
      });
      // Sort customers alphabetically by name
      const sortedCustomers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  // When user types in search, use server-side search with live balances
  useEffect(() => {
    if (searchQuery.trim().length < 2) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/customers/search`, {
          params: { q: searchQuery.trim(), include_calculated_balance: true }
        });
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Function to send payment receipt email
  const sendPaymentReceiptEmail = async (paymentId, email) => {
    try {
      setSendingEmail(true);
      await axios.post(`${API}/email/account-payment`, {
        payment_id: paymentId,
        recipient_email: email
      });
      toast.success(`Receipt emailed to ${email}`);
      return true;
    } catch (error) {
      console.error("Error sending receipt email:", error);
      toast.error("Failed to send receipt email");
      return false;
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle auto-send email after account payment
  const handleAutoSendEmail = async (payment) => {
    if (selectedCustomer?.email) {
      // Customer has email - send automatically
      await sendPaymentReceiptEmail(payment.id, selectedCustomer.email);
    } else {
      // No email - show prompt
      setEmailPromptAddress("");
      setShowEmailPrompt(true);
    }
  };

  // Handle email prompt submit
  const handleEmailPromptSubmit = async () => {
    if (!emailPromptAddress || !completedPayment) return;
    
    const success = await sendPaymentReceiptEmail(completedPayment.id, emailPromptAddress);
    if (success && selectedCustomer && !selectedCustomer.email) {
      // Optionally update customer's email in the system
      try {
        await axios.put(`${API}/customers/${selectedCustomer.id}`, {
          email: emailPromptAddress
        });
        toast.info("Customer email saved for future receipts");
      } catch (error) {
        console.error("Failed to save customer email:", error);
      }
    }
    setShowEmailPrompt(false);
  };

  // Handle email prompt cancel
  const handleEmailPromptCancel = () => {
    setShowEmailPrompt(false);
    toast.info("Receipt not emailed");
  };

  // Server-side search returns pre-filtered results with live balance;
  // client-side filter only applies to the initial full list (query < 2 chars)
  const filteredCustomers = searchQuery.trim().length >= 2
    ? customers  // server already filtered and included live balance
    : customers.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.account_number?.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleSelectCustomer = async (customer) => {
    // Switch to payment step immediately with cached data
    setSelectedCustomer(customer);
    setStep("payment");

    // Fetch live calculated balance from the individual customer endpoint
    setFetchingBalance(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}`);
      if (response.data) {
        setSelectedCustomer(response.data);
      }
    } catch (error) {
      console.error("Error fetching live customer balance:", error);
    } finally {
      setFetchingBalance(false);
    }
  };

  const handleKeypadPress = (value) => {
    const updateAmount = (current) => {
      if (value === "C") return "";
      if (value === "." && current.includes(".")) return current;
      return current + value;
    };

    switch (activeInput) {
      case "cash":
        setCashAmount(updateAmount(cashAmount));
        break;
      case "card":
        setCardAmount(updateAmount(cardAmount));
        break;
      case "eft":
        setEftAmount(updateAmount(eftAmount));
        break;
    }
  };

  const handleQuickAmount = (amount) => {
    switch (activeInput) {
      case "cash":
        setCashAmount(amount.toString());
        break;
      case "card":
        setCardAmount(amount.toString());
        break;
      case "eft":
        setEftAmount(amount.toString());
        break;
    }
  };

  const getTotalPaid = () => {
    return (parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) + (parseFloat(eftAmount) || 0);
  };

  const handleProcessPayment = async () => {
    const totalPaid = getTotalPaid();
    
    if (totalPaid <= 0) {
      toast.error("Please enter a payment amount");
      return;
    }

    setProcessing(true);

    try {
      // Determine primary payment method based on amounts
      let method = "CASH";
      if (parseFloat(cardAmount) > 0) method = "CARD";
      if (parseFloat(eftAmount) > 0) method = "EFT";
      if (parseFloat(cashAmount) > 0 && (parseFloat(cardAmount) > 0 || parseFloat(eftAmount) > 0)) {
        method = "SPLIT";
      }

      const paymentData = {
        customer_id: selectedCustomer.id,
        amount: totalPaid,
        payment_method: method,
        note: note || `Cash: R${cashAmount || 0}, Card: R${cardAmount || 0}, EFT: R${eftAmount || 0}`,
        cashier: "Cashier 1"
      };

      const response = await saveWithRetry(() => axios.post(`${API}/account-payments`, paymentData));
      
      toast.success(
        `Payment of R ${totalPaid.toFixed(2)} received. Receipt: ${response.data.receipt_number}`
      );
      
      // Show receipt
      setCompletedPayment(response.data);
      setShowReceipt(true);
      
      // Email receipt only when the cashier explicitly ticked the option.
      // No auto-send — this is the same opt-in behaviour as the customer-journal flow.
      if (emailReceiptToCustomer) {
        await handleAutoSendEmail(response.data);
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(errorMessage(error, "Failed to process payment"));
    } finally {
      setProcessing(false);
    }
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    setCompletedPayment(null);
    onClose();
  };

  const accountBalance = selectedCustomer?.calculated_balance ?? selectedCustomer?.account_balance ?? 0;

  // Show receipt modal after payment
  if (showReceipt && completedPayment) {
    return (
      <AccountPaymentReceiptModal
        open={showReceipt}
        onClose={handleReceiptClose}
        payment={completedPayment}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Receipt className="h-6 w-6 text-orange-600" />
            {step === "search" ? "Account Receipt - Select Customer" : "Process Receipt Payment"}
          </DialogTitle>
        </DialogHeader>

        {step === "search" ? (
          /* Customer Search Step */
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search by customer name or account number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-lg"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No customers found
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="p-4 cursor-pointer hover:bg-orange-50 transition-colors flex justify-between items-center"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <div className="font-semibold">{customer.name}</div>
                          <div className="text-sm text-gray-500">
                            {customer.account_number} • {customer.contact_number}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-bold text-lg ${(customer.calculated_balance ?? customer.account_balance ?? 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          R {(customer.calculated_balance ?? customer.account_balance ?? 0).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">Balance</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          </div>
        ) : (
          /* Payment Step */
          <div className="grid grid-cols-2 gap-6">
            {/* Left Side - Account Info & Payment Methods */}
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="p-4 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="font-bold text-lg">{selectedCustomer?.name}</div>
                    <div className="text-sm text-gray-600">{selectedCustomer?.account_number}</div>
                  </div>
                </div>
              </div>

              {/* Account Balance */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm text-orange-700 mb-1">Account Balance:</div>
                {fetchingBalance ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-orange-400" />
                    <span className="text-orange-500 text-sm">Fetching live balance...</span>
                  </div>
                ) : (
                  <div className={`text-3xl font-bold ${accountBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    R {accountBalance.toFixed(2)}
                  </div>
                )}
                {accountBalance > 0 && (
                  <div className="text-sm text-red-600 mt-1">Amount owing</div>
                )}
              </div>

              {/* Payment Methods */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-orange-700">RECEIPT</Label>
                
                {/* Cash */}
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    activeInput === "cash" ? "bg-green-100 border-2 border-green-500" : "bg-gray-50 border border-gray-200"
                  }`}
                  onClick={() => setActiveInput("cash")}
                >
                  <Banknote className="h-5 w-5 text-green-600" />
                  <span className="font-medium flex-1">CASH</span>
                  <Input
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    onFocus={() => setActiveInput("cash")}
                    className="w-32 text-right font-bold"
                    placeholder="0.00"
                  />
                </div>

                {/* Card */}
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    activeInput === "card" ? "bg-blue-100 border-2 border-blue-500" : "bg-gray-50 border border-gray-200"
                  }`}
                  onClick={() => setActiveInput("card")}
                >
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  <span className="font-medium flex-1">CARD</span>
                  <Input
                    value={cardAmount}
                    onChange={(e) => setCardAmount(e.target.value)}
                    onFocus={() => setActiveInput("card")}
                    className="w-32 text-right font-bold"
                    placeholder="0.00"
                  />
                </div>

                {/* EFT */}
                <div 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    activeInput === "eft" ? "bg-purple-100 border-2 border-purple-500" : "bg-gray-50 border border-gray-200"
                  }`}
                  onClick={() => setActiveInput("eft")}
                >
                  <Building className="h-5 w-5 text-purple-600" />
                  <span className="font-medium flex-1">EFT</span>
                  <Input
                    value={eftAmount}
                    onChange={(e) => setEftAmount(e.target.value)}
                    onFocus={() => setActiveInput("eft")}
                    className="w-32 text-right font-bold"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Total Paid */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm text-green-700 mb-1">Total Paid:</div>
                <div className="text-3xl font-bold text-green-700">
                  R {getTotalPaid().toFixed(2)}
                </div>
              </div>

              {/* Note */}
              <div>
                <Label className="mb-2 block">Note:</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note (optional)..."
                  className="min-h-[60px]"
                />
              </div>

              {/* Email receipt to customer — opt-in, never automatic */}
              <label className="flex items-start gap-2 cursor-pointer border rounded p-3 bg-blue-50 border-blue-200">
                <input
                  type="checkbox"
                  checked={emailReceiptToCustomer}
                  onChange={(e) => setEmailReceiptToCustomer(e.target.checked)}
                  className="mt-1 rounded"
                  data-testid="account-receipt-email-customer-checkbox"
                />
                <span className="text-sm">
                  <span className="flex items-center gap-1 font-medium text-blue-700">
                    <Mail className="h-4 w-4" />
                    Email this receipt to the customer
                  </span>
                  {selectedCustomer?.email ? (
                    <span className="block text-xs text-gray-600 mt-1">
                      Will be sent to <span className="font-mono">{selectedCustomer.email}</span>.
                    </span>
                  ) : (
                    <span className="block text-xs text-amber-700 mt-1">
                      Customer has no email on file — you&apos;ll be prompted for one after payment.
                    </span>
                  )}
                </span>
              </label>
            </div>

            {/* Right Side - Keypad */}
            <div className="space-y-3">
              {/* Numeric Keypad */}
              <div className="grid grid-cols-4 gap-2">
                {["7", "8", "9", "10"].map((key) => (
                  <Button
                    key={key}
                    variant="outline"
                    className={`h-14 text-xl font-bold ${key === "10" ? "bg-gray-200" : ""}`}
                    onClick={() => key === "10" ? handleQuickAmount(10) : handleKeypadPress(key)}
                  >
                    {key}
                  </Button>
                ))}
                {["4", "5", "6", "20"].map((key) => (
                  <Button
                    key={key}
                    variant="outline"
                    className={`h-14 text-xl font-bold ${key === "20" ? "bg-gray-200" : ""}`}
                    onClick={() => key === "20" ? handleQuickAmount(20) : handleKeypadPress(key)}
                  >
                    {key}
                  </Button>
                ))}
                {["1", "2", "3", "50"].map((key) => (
                  <Button
                    key={key}
                    variant="outline"
                    className={`h-14 text-xl font-bold ${key === "50" ? "bg-gray-200" : ""}`}
                    onClick={() => key === "50" ? handleQuickAmount(50) : handleKeypadPress(key)}
                  >
                    {key}
                  </Button>
                ))}
                {[".", "0", "C", "100"].map((key) => (
                  <Button
                    key={key}
                    variant="outline"
                    className={`h-14 text-xl font-bold ${
                      key === "C" ? "bg-red-100 text-red-600 hover:bg-red-200" : 
                      key === "100" ? "bg-gray-200" : ""
                    }`}
                    onClick={() => key === "100" ? handleQuickAmount(100) : handleKeypadPress(key)}
                  >
                    {key}
                  </Button>
                ))}
                <div className="col-span-3"></div>
                <Button
                  variant="outline"
                  className="h-14 text-xl font-bold bg-gray-200"
                  onClick={() => handleQuickAmount(200)}
                >
                  200
                </Button>
              </div>

              {/* Pay Full Balance Button */}
              {accountBalance > 0 && (
                <Button
                  variant="outline"
                  className="w-full h-12 border-orange-500 text-orange-600 hover:bg-orange-50"
                  onClick={() => {
                    if (activeInput === "cash") setCashAmount(accountBalance.toFixed(2));
                    if (activeInput === "card") setCardAmount(accountBalance.toFixed(2));
                    if (activeInput === "eft") setEftAmount(accountBalance.toFixed(2));
                  }}
                >
                  Pay Full Balance (R {accountBalance.toFixed(2)})
                </Button>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep("search")}
                  className="flex-1 h-14 text-lg"
                >
                  Back
                </Button>
                <Button
                  onClick={handleProcessPayment}
                  disabled={processing || getTotalPaid() <= 0}
                  className="flex-1 h-14 text-lg bg-green-600 hover:bg-green-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "ACCEPT"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Email Prompt Dialog - shown when customer has no email */}
      <Dialog open={showEmailPrompt} onOpenChange={setShowEmailPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Receipt via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              No email address on file for <strong>{selectedCustomer?.name}</strong>.
              Enter an email address to send the payment receipt, or cancel to skip.
            </p>
            <div>
              <Label htmlFor="email-prompt-receipt">Email Address</Label>
              <Input
                id="email-prompt-receipt"
                type="email"
                autoCapitalize={false}
                placeholder="customer@example.com"
                value={emailPromptAddress}
                onChange={(e) => setEmailPromptAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && emailPromptAddress) {
                    handleEmailPromptSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={handleEmailPromptCancel}
              disabled={sendingEmail}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEmailPromptSubmit}
              disabled={!emailPromptAddress || sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AccountReceiptModal;
